import React, { Suspense, lazy } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { Toaster } from './components/ui/sonner';

// Layout Components - Eager loaded (always needed)
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import Cart from './components/Cart';
import TrustPhoneBar from './components/TrustPhoneBar';
import ScrollToTop from './components/ScrollToTop';

// SEO Components - Eager loaded (needed for all pages)
import { OrganizationSchema, WebsiteSchema, ServiceSchema, LocalBusinessSchema } from './components/SEO';
import ForcePasswordChange from './components/ForcePasswordChange';

// Lazy loaded pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const PflegegradRechner = lazy(() => import('./pages/PflegegradRechner'));
const AnspruchPruefen = lazy(() => import('./pages/AnspruchPruefen'));
const Ratgeber = lazy(() => import('./pages/Ratgeber').then(module => ({ default: module.default })));
const BlogPost = lazy(() => import('./pages/Ratgeber').then(module => ({ default: module.BlogPost })));
const Login = lazy(() => import('./pages/Auth').then(module => ({ default: module.default })));
const Register = lazy(() => import('./pages/Auth').then(module => ({ default: module.Register })));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AntragStellen = lazy(() => import('./pages/AntragStellen'));
const AntragRedirect = lazy(() => import('./pages/AntragRedirect'));
const AntragErfolgreich = lazy(() => import('./pages/AntragErfolgreich'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Kontakt = lazy(() => import('./pages/Kontakt'));
const UeberUns = lazy(() => import('./pages/UeberUns'));
const FAQ = lazy(() => import('./pages/FAQ'));
const PflegeChatbot = lazy(() => import('./components/PflegeChatbot'));

// Legal Pages - Lazy loaded
const Impressum = lazy(() => import('./pages/legal/Impressum'));
const Datenschutz = lazy(() => import('./pages/legal/Datenschutz'));
const AGB = lazy(() => import('./pages/legal/AGB'));
const Widerruf = lazy(() => import('./pages/legal/Widerruf'));
const WiderrufFormular = lazy(() => import('./pages/legal/WiderrufFormular'));
const Barrierefreiheit = lazy(() => import('./pages/legal/Barrierefreiheit'));

// Embeddable Widget + Marketing Page
const PflegegradWidget = lazy(() => import('./pages/PflegegradWidget'));
const WidgetEmbedPage = lazy(() => import('./pages/WidgetEmbedPage'));

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
      <p className="text-muted-foreground text-sm">Laden...</p>
    </div>
  </div>
);

// WhatsApp Floating Button Component
const WhatsAppButton = () => {
  const whatsappNumber = "491784908678";
  const message = encodeURIComponent("Hallo! Ich interessiere mich für die Pflegebox von Medical π.");
  
  return (
    <a
      href={`https://wa.me/${whatsappNumber}?text=${message}`}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed right-3 sm:right-4 bottom-[80px] w-11 h-11 rounded-full bg-[#25D366]/80 hover:bg-[#25D366] backdrop-blur-md text-white shadow-md flex items-center justify-center z-40 transition-all hover:scale-105 ring-1 ring-white/30"
      aria-label="WhatsApp Chat öffnen"
      data-testid="whatsapp-button"
    >
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

// Layout wrapper that conditionally renders navbar/footer
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isWidgetEmbed = location.pathname.startsWith('/widget/'); // embedded widget only, NOT /widget marketing page
  const hideChrome = isAdminRoute || isWidgetEmbed;

  return (
    <>
      <ScrollToTop />
      {!hideChrome && <Navbar />}
      {!hideChrome && <TrustPhoneBar />}
      <main className={!hideChrome ? 'min-h-screen pt-[120px] md:pt-[140px] xl:pt-[120px]' : ''}>
        {children}
      </main>
      {!hideChrome && <Footer />}
      {!hideChrome && <WhatsAppButton />}
      {!hideChrome && (
        <Suspense fallback={null}>
          <PflegeChatbot />
        </Suspense>
      )}
      {!hideChrome && <Cart />}
      {!hideChrome && <CookieConsent />}
      <ForcePasswordChange />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          {/* Global SEO Schemas */}
          <OrganizationSchema />
          <WebsiteSchema />
          <ServiceSchema />
          <LocalBusinessSchema />
          
          <div className="App">
            <BrowserRouter>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  {/* Public Pages */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/pflegegrad-rechner" element={<PflegegradRechner />} />
                  <Route path="/anspruch-pruefen" element={<AnspruchPruefen />} />
                  <Route path="/pflegehilfsmittel" element={<AntragRedirect />} />
                  <Route path="/shop" element={<AntragRedirect />} />
                  <Route path="/ratgeber" element={<Ratgeber />} />
                  <Route path="/ratgeber/:slug" element={<BlogPost />} />
                  <Route path="/kontakt" element={<Kontakt />} />
                  <Route path="/ueber-uns" element={<UeberUns />} />
                  
                  {/* Auth Pages */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/registrieren" element={<Register />} />
                  
                  {/* Customer Portal */}
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/antrag" element={<AntragRedirect />} />
                  <Route path="/antrag/legacy" element={<AntragStellen />} />
                  <Route path="/antrag-erfolgreich" element={<AntragErfolgreich />} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/*" element={<AdminDashboard />} />
                  
                  {/* Legal Pages */}
                  <Route path="/impressum" element={<Impressum />} />
                  <Route path="/datenschutz" element={<Datenschutz />} />
                  <Route path="/agb" element={<AGB />} />
                  <Route path="/widerruf" element={<Widerruf />} />
                  <Route path="/widerruf-formular" element={<WiderrufFormular />} />
                  <Route path="/barrierefreiheit" element={<Barrierefreiheit />} />
                  <Route path="/faq" element={<FAQ />} />

                  {/* Embeddable Widget */}
                  <Route path="/widget" element={<WidgetEmbedPage />} />
                  <Route path="/widget/pflegegrad-check" element={<PflegegradWidget />} />
                  
                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </Layout>
          </BrowserRouter>
          <Toaster position="bottom-right" />
        </div>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

// 404 Page
const NotFound = () => (
  <div className="min-h-screen pt-24 pb-20 bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="text-8xl font-bold text-primary/20 font-[Manrope] mb-4">404</div>
      <h1 className="text-2xl font-bold text-foreground mb-4">Seite nicht gefunden</h1>
      <p className="text-muted-foreground mb-8">
        Die gesuchte Seite existiert leider nicht.
      </p>
      <a href="/" className="btn-primary">
        Zur Startseite
      </a>
    </div>
  </div>
);

export default App;
